<script lang="ts">
	import { format } from 'src/services/i18n';
	import type { Device } from 'src/types/Device';
	import {
		AdvancedCardV2,
		BulletedList,
		ButtonV2,
		CircleAvatar,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	import CameraRtspAddresses from './CameraRtspAddresses.svelte';
	import { getCameraConfig, saveCameraConfig } from '../../../helpers/camera-settings-helpers';

	export let device: Device;

	let enabled = getCameraConfig(device)?.rtsp?.enable ?? false;
	let loading = false;
	let showModal = false;
	/** After a successful enable the same modal hands over the stream addresses. */
	let step: 'confirm' | 'addresses' = 'confirm';

	$: securityNotes = [
		{
			title: $format('camera_rtsp_auth_heading', { default: 'Turn on Authentication' }),
			description: $format('camera_rtsp_auth_message', {
				default:
					'Without Authentication, anyone on your local network who knows the address can open the stream.',
			}),
		},
		{
			title: $format('camera_rtsp_encryption_heading', {
				default: 'RTSP streams are not encrypted',
			}),
			description: $format('camera_rtsp_encryption_message', {
				default: 'Anyone who can capture traffic on your network can view the video and audio.',
			}),
		},
		{
			title: $format('camera_rtsp_network_heading', { default: 'Keep it on your local network' }),
			description: $format('camera_rtsp_network_message', {
				default:
					'Never forward the RTSP port on your router, and only enable RTSP if you trust the devices on your network.',
			}),
		},
	];

	const setEnabled = async (value: boolean): Promise<boolean> => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ rtsp: { enable: value } },
			value
				? $format('camera_rtsp_enabled_toast', { default: 'RTSP enabled.' })
				: $format('camera_rtsp_disabled_toast', { default: 'RTSP disabled.' }),
		);
		if (success) enabled = value;
		loading = false;
		return success;
	};

	const openConfirm = () => {
		step = 'confirm';
		showModal = true;
	};

	const confirmEnable = async () => {
		if (await setEnabled(true)) {
			step = 'addresses';
		} else {
			showModal = false;
		}
	};
</script>

<AdvancedCardV2
	class="mb-m"
	aria-label={$format('camera_rtsp_title', { default: 'RTSP' })}
	title={$format('camera_rtsp_title', { default: 'RTSP' })}
	avatar={{ icon: 'fal fa-tower-broadcast' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_rtsp_desc', { default: 'Local live stream over RTSP' }),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		{#if enabled}
			<ButtonV2
				size="s"
				variant="danger-secondary"
				{loading}
				disabled={loading}
				on:click={() => setEnabled(false)}
				aria-label={$format('button_disable', { default: 'Disable' })}
			>
				{$format('button_disable', { default: 'Disable' })}
			</ButtonV2>
		{:else}
			<ButtonV2
				size="s"
				variant="secondary"
				disabled={loading}
				on:click={openConfirm}
				aria-label={$format('button_enable', { default: 'Enable' })}
			>
				{$format('button_enable', { default: 'Enable' })}
			</ButtonV2>
		{/if}
	</div>
</AdvancedCardV2>

{#if enabled}
	<div class="mb-m">
		<CameraRtspAddresses {device} />
	</div>
{/if}

<Modal
	{showModal}
	title={step === 'confirm'
		? $format('camera_rtsp_confirm_title', { default: 'Turn on RTSP?' })
		: $format('camera_rtsp_done_title', { default: 'RTSP is Enabled' })}
	backdropClose={false}
	on:close={() => (showModal = false)}
>
	<svelte:fragment slot="content">
		{#if step === 'confirm'}
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					size="xxl"
					icon="fa-thin fa-tower-broadcast"
					borderStatus={{ icon: 'fa-solid fa-triangle-exclamation', color: 'color-warning' }}
				/>
			</div>
			<Text class="mb-m text-center">
				{$format('camera_rtsp_confirm_message', {
					default:
						'RTSP lets video players and network recorders view this camera directly over your local network.',
				})}
			</Text>
			<Text styleType="label-large" class="mb-s">
				{$format('camera_rtsp_warning_heading', { default: 'RTSP stream security warning' })}
			</Text>
			<BulletedList list={securityNotes} titleStyleType="body-small" />
		{:else}
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					size="xxl"
					icon="fa-thin fa-tower-broadcast"
					borderStatus={{ icon: 'fa-solid fa-check', color: 'color-success' }}
				/>
			</div>
			<Text class="mb-m text-center">
				{$format('camera_rtsp_done_message', {
					default:
						'Use these addresses in your player or recorder. They only work on your local network. If Authentication is turned on, your player will ask for the camera credentials.',
				})}
			</Text>
			<CameraRtspAddresses {device} />
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		{#if step === 'confirm'}
			<ButtonV2
				class="mr-s"
				variant="secondary"
				disabled={loading}
				on:click={() => (showModal = false)}
				aria-label={$format('button_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				{loading}
				disabled={loading}
				on:click={confirmEnable}
				aria-label={$format('camera_rtsp_confirm_button', { default: 'Turn on RTSP' })}
			>
				{$format('camera_rtsp_confirm_button', { default: 'Turn on RTSP' })}
			</ButtonV2>
		{:else}
			<ButtonV2
				on:click={() => (showModal = false)}
				aria-label={$format('button_done', { default: 'Done' })}
			>
				{$format('button_done', { default: 'Done' })}
			</ButtonV2>
		{/if}
	</div>
</Modal>
